import { APP } from 'src/Configs/App'
import { rtkApi } from 'src/Services/rtkApi'
import { getValueOrZero } from 'src/Constants/functions'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'

type PaymentStatuses = 'payed' | 'completed' | 'charged-back'
type SpotifyCampaignsStatuses =
  | 'payment_pending'
  | 'pending_approval'
  | 'approved'
  | 'running'
  | 'scheduled'
  | 'ended'
  | 'canceled'
  | 'stopped'

type TiktokCampaignsStatuses =
  | 'awaiting_review'
  | 'canceled'
  | 'canceled_by_admin'
  | 'ended'
  | 'finished'
  | 'in_progress'
  | 'pending'
  | 'running'
  | 'scheduled'
  | 'stopped'
  | 'stopped_by_admin'

export interface Metadata {
  paymentsCountByStatus: Record<PaymentStatuses, number>
  spotifyCampaignsCountByStatus: Record<SpotifyCampaignsStatuses, number>
  tiktokCampaignsCountByStatus: Record<TiktokCampaignsStatuses, number>
}

export interface PaidCampaignsCount {
  count: number
}

export interface TotalPaidCampaignsCount {
  totalCount: number
}

const metadataApi = rtkApi.injectEndpoints({
  endpoints(build) {
    return {
      metadata: build.query<Metadata, void | null>({
        query: () => `${APP.TIKTOK_SERVER}/artist/any-campaign/metadata`,
      }),
      paidCampaignsCount: build.query<
        PaidCampaignsCount,
        CampaignPlatformTypeEnum
      >({
        query: (campaignType) =>
          `${APP.TIKTOK_SERVER}/artist/any-campaign/paid?campaignType=${campaignType}`,
      }),
      totalPaidCampaignsCount: build.query<TotalPaidCampaignsCount, void>({
        queryFn: async (_arg, _queryApi, _extraOptions, fetchWithBQ) => {
          const [tiktokResult, spotifyResult] = await Promise.allSettled([
            fetchWithBQ({
              url: `${APP.TIKTOK_SERVER}/artist/any-campaign/paid?campaignType=${CampaignPlatformTypeEnum.TIKTOK}`,
              method: 'GET',
            }),
            fetchWithBQ({
              url: `${APP.TIKTOK_SERVER}/artist/any-campaign/paid?campaignType=${CampaignPlatformTypeEnum.SPOTIFY}`,
              method: 'GET',
            }),
          ])

          const tiktokCount =
            tiktokResult.status === 'fulfilled' && !tiktokResult.value.error
              ? (tiktokResult.value.data as PaidCampaignsCount).count
              : 0

          const spotifyCount =
            spotifyResult.status === 'fulfilled' && !spotifyResult.value.error
              ? (spotifyResult.value.data as PaidCampaignsCount).count
              : 0

          return {
            data: {
              totalCount:
                getValueOrZero(tiktokCount) + getValueOrZero(spotifyCount),
            },
          }
        },
      }),
    }
  },
})

export const {
  useMetadataQuery,
  usePaidCampaignsCountQuery,
  useTotalPaidCampaignsCountQuery,
} = metadataApi

const CHARGE_BACK = 'charged-back'

export const getAllCampaignsCount = (state?: Metadata): number => {
  const tikTokCampaignsCount = Object.entries(
    state?.tiktokCampaignsCountByStatus || {},
  ).reduce((accumulator, [, value]) => accumulator + getValueOrZero(value), 0)

  const spotifyCampaignsCount = Object.entries(
    state?.spotifyCampaignsCountByStatus || {},
  ).reduce((accumulator, [, value]) => accumulator + getValueOrZero(value), 0)

  return tikTokCampaignsCount + spotifyCampaignsCount
}

export const getHasPaidCampaign = (
  paidCampaignsCount?: PaidCampaignsCount,
): boolean => getValueOrZero(paidCampaignsCount?.count) > 0

export const getHasOnePaidCampaign = (
  paidCampaignsCount?: PaidCampaignsCount,
): boolean => getValueOrZero(paidCampaignsCount?.count) === 1

export const getIsCreateTikTokCampaignAvailableSelector = (
  state?: Metadata,
): boolean => {
  if (!state) {
    return false
  }

  const payedSpotifyCampaignsNumbers = [
    state.spotifyCampaignsCountByStatus?.ended,
    state.spotifyCampaignsCountByStatus?.running,
    state.spotifyCampaignsCountByStatus?.scheduled,
  ].reduce((accumulator, value) => accumulator + getValueOrZero(value), 0)

  const payedTikTokCampaignsNumbers = [
    state.tiktokCampaignsCountByStatus?.ended,
    state.tiktokCampaignsCountByStatus?.scheduled,
    state.tiktokCampaignsCountByStatus?.running,
    state.tiktokCampaignsCountByStatus?.in_progress,
    state.tiktokCampaignsCountByStatus?.canceled,
    state.tiktokCampaignsCountByStatus?.stopped,
  ].reduce((accumulator, value) => accumulator + getValueOrZero(value), 0)

  const isStoppedCanceledCampaignsNumbers = [
    state.spotifyCampaignsCountByStatus?.canceled,
    state.spotifyCampaignsCountByStatus?.stopped,
    state.tiktokCampaignsCountByStatus?.canceled,
    state.tiktokCampaignsCountByStatus?.stopped,
  ].reduce((accumulator, value) => accumulator + getValueOrZero(value), 0)

  const isNoStoppedCanceledCampaigns = isStoppedCanceledCampaignsNumbers === 0
  const isNoPayedTikTokCampaign = payedTikTokCampaignsNumbers === 0
  const isPayedSpotifyCampaign = payedSpotifyCampaignsNumbers > 1
  const noChargebacks = state?.paymentsCountByStatus[CHARGE_BACK] === 0

  return (
    noChargebacks &&
    isNoPayedTikTokCampaign &&
    isNoStoppedCanceledCampaigns &&
    isPayedSpotifyCampaign
  )
}

export const hasPayedTikTokCampaigns = (state?: Metadata): boolean => {
  if (!state) {
    return false
  }
  const payedTikTokCampaignsNumbers = [
    state.tiktokCampaignsCountByStatus?.ended,
    state.tiktokCampaignsCountByStatus?.scheduled,
    state.tiktokCampaignsCountByStatus?.running,
    state.tiktokCampaignsCountByStatus?.in_progress,
    state.tiktokCampaignsCountByStatus?.canceled,
    state.tiktokCampaignsCountByStatus?.stopped,
  ].reduce((accumulator, value) => accumulator + getValueOrZero(value), 0)
  return payedTikTokCampaignsNumbers > 0
}

export const getIsDiscoverTikTokCampaignAvailableSelector = (
  state?: Metadata,
): boolean => {
  if (!state) {
    return false
  }

  const payedSpotifyCampaignsStatuses = [
    state.spotifyCampaignsCountByStatus?.ended,
    state.spotifyCampaignsCountByStatus?.running,
    state.spotifyCampaignsCountByStatus?.scheduled,
  ].reduce((accumulator, value) => accumulator + getValueOrZero(value), 0)

  return payedSpotifyCampaignsStatuses === 1 && !hasPayedTikTokCampaigns(state)
}
